import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Note, UpsertNoteDTO, UpsertRunningNoteDTO } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
export const noteKeys = {
  all: ["notes"] as const,
  byMonth: (month: string) => [...noteKeys.all, "month", month] as const,
  running: ["notes", "running"] as const,
  bySlug: (slug: string) => [...noteKeys.running, slug] as const,
};

// Get note by month
export function useNoteByMonth(month: string) {
  return useQuery({
    queryKey: noteKeys.byMonth(month),
    queryFn: () => apiClient.getNoteByMonth(month),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Upsert note (create or update)
export function useUpsertNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertNoteDTO) => apiClient.upsertNote(data),
    onMutate: async (data) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: noteKeys.byMonth(data.month),
      });

      // Snapshot the previous value
      const previousNote = queryClient.getQueryData<Note | null>(noteKeys.byMonth(data.month));

      // Optimistically update to the new value
      queryClient.setQueryData<Note | null>(noteKeys.byMonth(data.month), (old) => {
        if (old) {
          return {
            ...old,
            content: data.content,
            updatedAt: new Date().toISOString(),
          };
        }
        // If no previous note, create a temporary one
        return {
          id: 0,
          noteType: "monthly" as const,
          month: data.month,
          content: data.content || null,
          date: new Date().toISOString(),
          source: "web_app" as const,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          meta: null,
        };
      });

      return { previousNote };
    },
    onError: (error: Error, data, context) => {
      // Rollback on error
      if (context?.previousNote !== undefined) {
        queryClient.setQueryData(noteKeys.byMonth(data.month), context.previousNote);
      }

      toast({
        title: "Error",
        description: error.message || "Failed to save note",
        variant: "destructive",
      });
    },
    // Note: We intentionally don't invalidate queries here.
    // The optimistic update handles the UI state, and refetching
    // causes glitchy behavior in the editor by resetting content.
  });
}

// ==================
// Running Notes
// ==================

// Get running note by slug
export function useRunningNote(slug: string) {
  return useQuery({
    queryKey: noteKeys.bySlug(slug),
    queryFn: () => apiClient.getRunningNote(slug),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Upsert running note (create or update)
export function useUpsertRunningNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertRunningNoteDTO) => apiClient.upsertRunningNote(data),
    onMutate: async (data) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: noteKeys.bySlug(data.slug),
      });

      // Snapshot the previous value
      const previousNote = queryClient.getQueryData<Note | null>(noteKeys.bySlug(data.slug));

      // Optimistically update to the new value
      queryClient.setQueryData<Note | null>(noteKeys.bySlug(data.slug), (old) => {
        if (old) {
          return {
            ...old,
            content: data.content,
            updatedAt: new Date().toISOString(),
          };
        }
        // If no previous note, create a temporary one
        return {
          id: 0,
          noteType: "running" as const,
          slug: data.slug,
          content: data.content || null,
          date: new Date().toISOString(),
          source: "web_app" as const,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          meta: null,
        };
      });

      return { previousNote };
    },
    onError: (error: Error, data, context) => {
      // Rollback on error
      if (context?.previousNote !== undefined) {
        queryClient.setQueryData(noteKeys.bySlug(data.slug), context.previousNote);
      }

      toast({
        title: "Error",
        description: error.message || "Failed to save running note",
        variant: "destructive",
      });
    },
    // Note: We intentionally don't invalidate queries here.
    // The optimistic update handles the UI state, and refetching
    // causes glitchy behavior in the editor by resetting content.
  });
}
